import { WorkModel } from "../../../factories/works/models/work.model";
import {
  clientKey,
  daysBetween,
  entryClient,
  entryHours,
  entryPerson,
  entryValue,
  entryWorkType,
  formatHours,
  formatMoney,
  InvoiceLike,
  isBillableEntry,
  isOverdue,
  NOT_ASSIGNED,
  peopleOf,
  startOfDay,
  statusGroup,
  TimeEntry,
  trackedHours,
} from "../reports.utils";
import {
  Compare,
  GroupBy,
  isTimeBucket,
  Metric,
  METRIC_INFO,
  metricLabel,
  RangePreset,
  ReportSpec,
  ValueKind,
} from "./reportSpec";

/**
 * Turns a ReportSpec + the loaded lists into a table (pure; reportEngine.test.ts).
 * Numbers are computed here, never by the AI.
 */

export type Range = { from: Date; to: Date } | null;

const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const addDays = (date: Date, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

/** Monday of the date's week (weeks run Monday to Sunday, like the rest of the app). */
export const weekStart = (date: Date) => {
  const d = startOfDay(date);
  const day = (d.getDay() + 6) % 7;
  return addDays(d, -day);
};

export const rangeOf = (
  preset: RangePreset,
  today: Date = new Date(),
): Range => {
  const y = today.getFullYear();
  const m = today.getMonth();
  const q = Math.floor(m / 3) * 3;
  switch (preset) {
    case "this_week": {
      const from = weekStart(today);
      return { from, to: endOfDay(addDays(from, 6)) };
    }
    case "last_week": {
      const from = addDays(weekStart(today), -7);
      return { from, to: endOfDay(addDays(from, 6)) };
    }
    case "this_month":
      return { from: new Date(y, m, 1), to: endOfDay(new Date(y, m + 1, 0)) };
    case "last_month":
      return { from: new Date(y, m - 1, 1), to: endOfDay(new Date(y, m, 0)) };
    case "this_quarter":
      return { from: new Date(y, q, 1), to: endOfDay(new Date(y, q + 3, 0)) };
    case "last_quarter":
      return { from: new Date(y, q - 3, 1), to: endOfDay(new Date(y, q, 0)) };
    case "this_year":
      return { from: new Date(y, 0, 1), to: endOfDay(new Date(y, 11, 31)) };
    case "last_year":
      return {
        from: new Date(y - 1, 0, 1),
        to: endOfDay(new Date(y - 1, 11, 31)),
      };
    case "last_30_days":
      return { from: startOfDay(addDays(today, -29)), to: endOfDay(today) };
    case "last_90_days":
      return { from: startOfDay(addDays(today, -89)), to: endOfDay(today) };
    case "last_12_months":
      return { from: new Date(y, m - 11, 1), to: endOfDay(today) };
    default:
      return null;
  }
};

const shiftMonths = (date: Date, months: number, end: boolean) => {
  // Keeps month ends on month ends (Feb 29 -> Feb 28).
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const last = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0,
  ).getDate();
  const isLastDay =
    date.getDate() ===
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const day = end && isLastDay ? last : Math.min(date.getDate(), last);
  const d = new Date(target.getFullYear(), target.getMonth(), day);
  return end ? endOfDay(d) : d;
};

const PERIOD_MONTHS: Partial<Record<RangePreset, number>> = {
  this_month: 1,
  last_month: 1,
  this_quarter: 3,
  last_quarter: 3,
  this_year: 12,
  last_year: 12,
  last_12_months: 12,
};

/** Dates that count as "the past" for comparisons: the current period stops at today. */
const PAST_FIELDS = ["date", "completed", "invoice_date"];

/**
 * The current range and the one it is compared with. With a comparison, a period that
 * runs into the future (this year, this month…) is cut at today for dates that can only be
 * in the past, so "this year vs last year" compares the same days.
 */
export const reportRanges = (
  spec: Pick<ReportSpec, "range" | "compare" | "dateField">,
  today: Date = new Date(),
): { current: Range; previous: Range } => {
  const base = rangeOf(spec.range, today);
  if (!base || spec.compare === "none")
    return { current: base, previous: null };
  const current =
    PAST_FIELDS.includes(spec.dateField) && base.to > endOfDay(today)
      ? { from: base.from, to: endOfDay(today) }
      : base;
  const compare: Compare = spec.compare;
  if (compare === "same_period_last_year") {
    return {
      current,
      previous: {
        from: shiftMonths(current.from, -12, false),
        to: shiftMonths(current.to, -12, true),
      },
    };
  }
  const months = PERIOD_MONTHS[spec.range];
  if (months) {
    return {
      current,
      previous: {
        from: shiftMonths(current.from, -months, false),
        to: shiftMonths(current.to, -months, true),
      },
    };
  }
  const days =
    Math.round(
      (startOfDay(base.to).getTime() - base.from.getTime()) / 86400000,
    ) + 1;
  return {
    current,
    previous: {
      from: addDays(current.from, -days),
      to: endOfDay(addDays(startOfDay(current.to), -days)),
    },
  };
};

const toDate = (value?: string | Date | null) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const inRange = (date: Date | null, range: Range) =>
  !!date && (!range || (date >= range.from && date <= range.to));

// ------------------------------------------------------------------ buckets

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const pad = (n: number) => String(n).padStart(2, "0");
const monthKeyOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const weekKeyOf = (d: Date) => {
  const w = weekStart(d);
  return `${w.getFullYear()}-${pad(w.getMonth() + 1)}-${pad(w.getDate())}`;
};

const bucketLabel = (
  group: "month" | "week",
  key: string,
  multiYear: boolean,
) => {
  const [y, m, d] = key.split("-").map(Number);
  if (group === "month") return `${MONTHS[m - 1]}${multiYear ? ` ${y}` : ""}`;
  return `${MONTHS[m - 1]} ${d}${multiYear ? `, ${y}` : ""}`;
};

/** Every month / week of the range (so empty ones show as 0), at most 60. */
const bucketsOf = (group: "month" | "week", range: Range, today: Date) => {
  if (!range) return [];
  const keys: string[] = [];
  const stop = range.to;
  if (group === "month") {
    const c = new Date(range.from.getFullYear(), range.from.getMonth(), 1);
    while (c <= stop && keys.length < 60) {
      keys.push(monthKeyOf(c));
      c.setMonth(c.getMonth() + 1);
    }
  } else {
    let c = weekStart(range.from);
    while (c <= stop && keys.length < 60) {
      keys.push(weekKeyOf(c));
      c = addDays(c, 7);
    }
  }
  void today;
  return keys;
};

// ------------------------------------------------------------------ accumulate

type Acc = {
  hours: number;
  billable: number;
  nonBillable: number;
  unbilled: number;
  value: number;
  unbilledValue: number;
  entries: number;
  count: number;
  overdue: number;
  completed: number;
  onTime: number;
  daysSum: number;
  daysN: number;
  budget: number;
  tracked: number;
  overBudget: number;
  total: number;
  balance: number;
  overdueBalance: number;
};

const emptyAcc = (): Acc => ({
  hours: 0,
  billable: 0,
  nonBillable: 0,
  unbilled: 0,
  value: 0,
  unbilledValue: 0,
  entries: 0,
  count: 0,
  overdue: 0,
  completed: 0,
  onTime: 0,
  daysSum: 0,
  daysN: 0,
  budget: 0,
  tracked: 0,
  overBudget: 0,
  total: 0,
  balance: 0,
  overdueBalance: 0,
});

const addAcc = (a: Acc, b: Acc) => {
  (Object.keys(a) as (keyof Acc)[]).forEach((k) => {
    a[k] += b[k];
  });
};

export const metricValue = (acc: Acc, metric: Metric): number | null => {
  switch (metric) {
    case "hours":
      return acc.hours;
    case "billable_hours":
      return acc.billable;
    case "non_billable_hours":
      return acc.nonBillable;
    case "unbilled_hours":
      return acc.unbilled;
    case "value":
      return acc.value;
    case "unbilled_value":
      return acc.unbilledValue;
    case "entries":
      return acc.entries;
    case "avg_hours_per_entry":
      return acc.entries ? acc.hours / acc.entries : null;
    case "count":
      return acc.count;
    case "overdue":
      return acc.overdue;
    case "completed":
      return acc.completed;
    case "completed_on_time_pct":
      return acc.completed ? (acc.onTime / acc.completed) * 100 : null;
    case "avg_days_to_complete":
      return acc.daysN ? acc.daysSum / acc.daysN : null;
    case "budget_hours":
      return acc.budget;
    case "tracked_hours":
      return acc.tracked;
    case "over_budget":
      return acc.overBudget;
    case "total":
      return acc.total;
    case "paid":
      return acc.total - acc.balance;
    case "balance":
      return acc.balance;
    case "overdue_balance":
      return acc.overdueBalance;
    case "avg_invoice":
      return acc.count ? acc.total / acc.count : null;
    default:
      return null;
  }
};

type Keyed = { key: string; label: string; link?: string };

type Item = {
  acc: Acc;
  date: Date | null;
  groups: Keyed[];
  split: Keyed | null;
};

const UNPAID = 0.005;

const invoiceStatusOf = (inv: InvoiceLike, today: Date) => {
  if ((inv.balance || 0) <= UNPAID) return "Paid";
  const due = toDate(inv.dueDate);
  if (due && due < startOfDay(today)) return "Overdue";
  return (inv.balance || 0) < (inv.total || 0) - UNPAID
    ? "Partially paid"
    : "Not due yet";
};

const clientLink = (w: WorkModel) =>
  w.businessContactId
    ? `/organization/${w.businessContactId}`
    : w.contactId && w.customerId
    ? `/contact/${w.customerId}?contactId=${w.contactId}`
    : undefined;

const timeItems = (spec: ReportSpec, entries: TimeEntry[]): Item[] =>
  entries
    .filter((e) => {
      const billable = isBillableEntry(e);
      if (spec.filters.billable === "billable" && !billable) return false;
      if (spec.filters.billable === "non_billable" && billable) return false;
      if (spec.filters.billed === "billed" && !e.isBilled) return false;
      if (spec.filters.billed === "unbilled" && (e.isBilled || !billable))
        return false;
      return true;
    })
    .map((e) => {
      const h = entryHours(e);
      const billable = isBillableEntry(e);
      const unbilled = billable && !e.isBilled;
      const value = billable ? entryValue(e) : 0;
      const acc = emptyAcc();
      acc.hours = h;
      acc.billable = billable ? h : 0;
      acc.nonBillable = billable ? 0 : h;
      acc.unbilled = unbilled ? h : 0;
      acc.value = value;
      acc.unbilledValue = unbilled ? value : 0;
      acc.entries = 1;
      const date = toDate(e.date);
      const group = (by: GroupBy): Keyed[] => {
        switch (by) {
          case "person":
            return [{ key: e.assignedUserId || "?", label: entryPerson(e) }];
          case "client":
            return [{ key: clientKey(e), label: entryClient(e) }];
          case "work_type": {
            const name = entryWorkType(e);
            return [{ key: e.workTypeId || `name:${name}`, label: name }];
          }
          case "work": {
            const name = e.work?.name || "No work item";
            return [{ key: `name:${name}`, label: name }];
          }
          default:
            return [];
        }
      };
      const split: Keyed | null =
        spec.splitBy === "billable"
          ? billable
            ? { key: "billable", label: "Billable" }
            : { key: "non_billable", label: "Non-billable" }
          : null;
      return { acc, date, groups: group(spec.groupBy), split };
    });

const workDate = (w: WorkModel, field: string) =>
  toDate(
    field === "start"
      ? w.startDate
      : field === "completed"
      ? w.statusUpdatedAt
      : w.dueDate,
  );

const workItems = (spec: ReportSpec, works: WorkModel[], today: Date): Item[] =>
  works
    .filter((w) => {
      const group = statusGroup(w);
      const open = group !== "Completed";
      switch (spec.filters.workStatus) {
        case "open":
          return open;
        case "completed":
          return !open;
        case "overdue":
          return open && isOverdue(w, today);
        default:
          return true;
      }
    })
    .map((w) => {
      const group = statusGroup(w);
      const open = group !== "Completed";
      const acc = emptyAcc();
      acc.count = 1;
      acc.overdue = open && isOverdue(w, today) ? 1 : 0;
      if (!open) {
        acc.completed = 1;
        const at = toDate(w.statusUpdatedAt);
        const due = toDate(w.dueDate);
        if (at && (!due || at <= endOfDay(due))) acc.onTime = 1;
        const start = toDate(w.startDate);
        if (at && start) {
          acc.daysSum = Math.max(0, daysBetween(start, at));
          acc.daysN = 1;
        }
      }
      const budget = w.budgetedTime || 0;
      const tracked = trackedHours(w);
      acc.budget = budget;
      acc.tracked = tracked;
      acc.overBudget = budget > 0 && tracked > budget ? 1 : 0;
      const groups = (by: GroupBy): Keyed[] => {
        switch (by) {
          case "assignee":
            return peopleOf(w).map((p) => ({
              key: p.id || "none",
              label: p.id ? p.name : NOT_ASSIGNED.name,
            }));
          case "client":
            return [
              {
                key: w.customerId || `name:${w.customerName}`,
                label: w.customerName || "No client",
                link: clientLink(w),
              },
            ];
          case "work_type":
            return [
              {
                key: w.workTypeId || `name:${w.workType}`,
                label: w.workType || "No work type",
              },
            ];
          case "status":
            return [{ key: group, label: group }];
          default:
            return [];
        }
      };
      const split: Keyed | null =
        spec.splitBy === "status" ? { key: group, label: group } : null;
      return {
        acc,
        date: workDate(w, spec.dateField),
        groups: groups(spec.groupBy),
        split,
      };
    });

const invoiceItems = (
  spec: ReportSpec,
  invoices: InvoiceLike[],
  today: Date,
): Item[] =>
  invoices
    .filter((inv) => {
      const status = invoiceStatusOf(inv, today);
      switch (spec.filters.invoiceStatus) {
        case "paid":
          return status === "Paid";
        case "unpaid":
          return status !== "Paid";
        case "overdue":
          return status === "Overdue";
        default:
          return true;
      }
    })
    .map((inv) => {
      const status = invoiceStatusOf(inv, today);
      const acc = emptyAcc();
      acc.count = 1;
      acc.total = inv.total || 0;
      acc.balance = inv.balance || 0;
      acc.overdueBalance = status === "Overdue" ? inv.balance || 0 : 0;
      const groups = (by: GroupBy): Keyed[] => {
        switch (by) {
          case "client":
            return [
              {
                key: inv.customerId || `name:${inv.customerName}`,
                label: inv.customerName || "No client",
              },
            ];
          case "assignee": {
            const name = `${inv.userFirstName || ""} ${
              inv.userLastName || ""
            }`.trim();
            return [
              { key: inv.userId || "none", label: name || "No assignee" },
            ];
          }
          case "status":
            return [{ key: status, label: status }];
          default:
            return [];
        }
      };
      return {
        acc,
        date: toDate(
          spec.dateField === "due_date" ? inv.dueDate : inv.invoiceDate,
        ),
        groups: groups(spec.groupBy),
        split: null,
      };
    });

// ------------------------------------------------------------------ result

export type ResultColumn = {
  key: string;
  label: string;
  kind: ValueKind;
  metric: Metric;
  role: "value" | "previous" | "change";
};

export type ResultRow = {
  key: string;
  label: string;
  link?: string;
  values: Record<string, number | null>;
  /** First metric per split key (stacked chart / split columns). */
  split: Record<string, number>;
};

export type ReportResult = {
  columns: ResultColumn[];
  rows: ResultRow[];
  totals: Record<string, number | null>;
  splitKeys: { key: string; label: string }[];
  /** Rows left out by the spec's limit. */
  hiddenRows: number;
  previousLabel: string | null;
  itemCount: number;
};

export type ReportData = {
  time?: TimeEntry[];
  work?: WorkModel[];
  invoices?: InvoiceLike[];
};

const itemsOf = (
  spec: ReportSpec,
  data: ReportData,
  range: Range,
  today: Date,
) => {
  const all =
    spec.source === "time"
      ? timeItems(spec, data.time ?? [])
      : spec.source === "work"
      ? workItems(spec, data.work ?? [], today)
      : invoiceItems(spec, data.invoices ?? [], today);
  return all.filter((i) => inRange(i.date, range));
};

type Group = Keyed & { acc: Acc; split: Map<string, Acc> };

const groupItems = (spec: ReportSpec, items: Item[]) => {
  const groups = new Map<string, Group>();
  const splitLabels = new Map<string, string>();
  items.forEach((item) => {
    const keys: Keyed[] =
      spec.groupBy === "month" || spec.groupBy === "week"
        ? item.date
          ? [
              {
                key:
                  spec.groupBy === "month"
                    ? monthKeyOf(item.date)
                    : weekKeyOf(item.date),
                label: "",
              },
            ]
          : []
        : item.groups;
    const split: Keyed | null =
      spec.splitBy === "month" && item.date
        ? { key: monthKeyOf(item.date), label: "" }
        : item.split;
    keys.forEach((k) => {
      let g = groups.get(k.key);
      if (!g) {
        g = { ...k, acc: emptyAcc(), split: new Map() };
        groups.set(k.key, g);
      }
      addAcc(g.acc, item.acc);
      if (split) {
        splitLabels.set(split.key, split.label);
        let s = g.split.get(split.key);
        if (!s) {
          s = emptyAcc();
          g.split.set(split.key, s);
        }
        addAcc(s, item.acc);
      }
    });
  });
  return { groups, splitLabels };
};

const STATUS_ORDER = [
  "Ready",
  "In Progress",
  "Waiting",
  "Scheduled",
  "Completed",
  "Not due yet",
  "Partially paid",
  "Overdue",
  "Paid",
];

export const computeReport = (
  spec: ReportSpec,
  data: ReportData,
  previousData: ReportData | null,
  today: Date = new Date(),
): ReportResult => {
  const { current, previous } = reportRanges(spec, today);
  const items = itemsOf(spec, data, current, today);
  const { groups, splitLabels } = groupItems(spec, items);
  const hasPrevious = spec.compare !== "none" && !!previous;
  const prevItems = hasPrevious
    ? itemsOf(spec, previousData ?? data, previous, today)
    : [];
  const prev = hasPrevious
    ? groupItems(spec, prevItems).groups
    : new Map<string, Group>();

  const bucket = isTimeBucket(spec.groupBy)
    ? (spec.groupBy as "month" | "week")
    : null;
  // Months / weeks: every bucket of the range, in order (compare = same position).
  let keys: string[];
  // Dates that can only be in the past stop at today (no empty future months).
  const bucketRange =
    current &&
    PAST_FIELDS.includes(spec.dateField) &&
    current.to > endOfDay(today)
      ? { from: current.from, to: endOfDay(today) }
      : current;
  if (bucket) {
    const all = bucketsOf(bucket, bucketRange, today);
    const extra = Array.from(groups.keys()).filter((k) => !all.includes(k));
    keys = [...all, ...extra].sort();
  } else {
    keys = Array.from(groups.keys());
  }
  const prevBucketKeys = bucket
    ? (() => {
        const all = bucketsOf(bucket, previous, today);
        const extra = Array.from(prev.keys()).filter((k) => !all.includes(k));
        return [...all, ...extra].sort();
      })()
    : [];
  const multiYear =
    !!bucket && new Set(keys.map((k) => k.slice(0, 4))).size > 1;

  const previousLabel = hasPrevious
    ? spec.compare === "same_period_last_year"
      ? "Last year"
      : "Previous"
    : null;

  const compared = hasPrevious ? spec.metrics.slice(0, 2) : [];
  const columns: ResultColumn[] = [];
  spec.metrics.forEach((metric) => {
    const kind = METRIC_INFO[metric].kind;
    const label = metricLabel(metric, spec.source);
    columns.push({ key: metric, label, kind, metric, role: "value" });
    if (compared.includes(metric)) {
      columns.push({
        key: `${metric}__prev`,
        label: `${label} (${previousLabel?.toLowerCase()})`,
        kind,
        metric,
        role: "previous",
      });
      columns.push({
        key: `${metric}__change`,
        label: "Change",
        kind,
        metric,
        role: "change",
      });
    }
  });

  const first = spec.metrics[0];
  let rows: ResultRow[] = keys.map((key, index) => {
    const g = groups.get(key);
    const acc = g?.acc ?? emptyAcc();
    const p = bucket ? prev.get(prevBucketKeys[index]) : prev.get(key);
    const values: Record<string, number | null> = {};
    spec.metrics.forEach((metric) => {
      const v = metricValue(acc, metric);
      values[metric] = v;
      if (compared.includes(metric)) {
        const pv = p
          ? metricValue(p.acc, metric)
          : metric === "completed_on_time_pct" ||
            metric === "avg_days_to_complete" ||
            metric === "avg_hours_per_entry" ||
            metric === "avg_invoice"
          ? null
          : 0;
        values[`${metric}__prev`] = pv;
        values[`${metric}__change`] = v !== null && pv !== null ? v - pv : null;
      }
    });
    const split: Record<string, number> = {};
    g?.split.forEach((s, k) => {
      split[k] = metricValue(s, first) ?? 0;
    });
    return {
      key,
      label: bucket ? bucketLabel(bucket, key, multiYear) : g?.label || key,
      link: g?.link,
      values,
      split,
    };
  });

  // Order.
  const sortBy = spec.sort.by;
  const dir = spec.sort.dir === "asc" ? 1 : -1;
  if (sortBy === "label") {
    if (bucket) {
      rows.sort((a, b) => (a.key < b.key ? -1 : 1) * dir);
    } else if (spec.groupBy === "status") {
      rows.sort(
        (a, b) =>
          (STATUS_ORDER.indexOf(a.key) - STATUS_ORDER.indexOf(b.key)) * dir,
      );
    } else {
      rows.sort((a, b) => a.label.localeCompare(b.label) * dir);
    }
  } else {
    rows.sort((a, b) => {
      const av = a.values[sortBy];
      const bv = b.values[sortBy];
      if (av === null && bv === null) return a.label.localeCompare(b.label);
      if (av === null) return 1;
      if (bv === null) return -1;
      return (av - bv) * dir || a.label.localeCompare(b.label);
    });
  }

  // Totals over every row (not only the shown ones).
  const totalAcc = emptyAcc();
  items.forEach((i) => addAcc(totalAcc, i.acc));
  // People count once per work item even with two assignees: totals come from the items.
  const prevAcc = emptyAcc();
  prevItems.forEach((i) => addAcc(prevAcc, i.acc));
  const totals: Record<string, number | null> = {};
  spec.metrics.forEach((metric) => {
    const v = metricValue(totalAcc, metric);
    totals[metric] = v;
    if (compared.includes(metric)) {
      const pv = metricValue(prevAcc, metric);
      totals[`${metric}__prev`] = pv;
      totals[`${metric}__change`] = v !== null && pv !== null ? v - pv : null;
    }
  });

  const hiddenRows = bucket ? 0 : Math.max(0, rows.length - spec.limit);
  if (!bucket) rows = rows.slice(0, spec.limit);

  const splitKeys = Array.from(splitLabels.keys())
    .sort((a, b) =>
      spec.splitBy === "month"
        ? a < b
          ? -1
          : 1
        : STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b) ||
          (a === "billable" ? -1 : 1),
    )
    .map((key) => ({
      key,
      label:
        spec.splitBy === "month"
          ? bucketLabel(
              "month",
              key,
              new Set(Array.from(splitLabels.keys()).map((k) => k.slice(0, 4)))
                .size > 1,
            )
          : splitLabels.get(key) || key,
    }));

  return {
    columns,
    rows,
    totals,
    splitKeys,
    hiddenRows,
    previousLabel,
    itemCount: items.length,
  };
};

// ------------------------------------------------------------------ format

export const formatValue = (
  kind: ValueKind,
  value: number | null | undefined,
) => {
  if (value === null || value === undefined || !Number.isFinite(value))
    return "—";
  switch (kind) {
    case "hours":
      return formatHours(value);
    case "money":
      return formatMoney(value);
    case "days":
      return `${(Math.round(value * 10) / 10).toLocaleString("en-US")} days`;
    case "percent":
      return `${Math.round(value)}%`;
    default:
      return Math.round(value).toLocaleString("en-US");
  }
};

/** "+3.5 h" / "−$1,200" / "+2". */
export const formatChange = (
  kind: ValueKind,
  value: number | null | undefined,
) => {
  if (value === null || value === undefined || !Number.isFinite(value))
    return "—";
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  const text = formatValue(
    kind === "percent" ? "count" : kind,
    Math.abs(value),
  );
  return `${sign}${text}${kind === "percent" ? " pts" : ""}`;
};
